import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { SgSessionStorageService } from 'saga-modules';
import { events } from 'gw-platform-events-js';
import cookie from 'js-cookie';
import queryString from 'query-string';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';

const CALLBACK_URL = './callback';
const REQUEST_UNAUTHORIZED_EVENT = 'request::unauthorized';

const getAuthConfig = () => {
    function getEnvironment() {
        const host = window.location.hostname;

        switch (host) {
            case 'www.saga.co.uk':
                return 'prod';
            case 'preprod.saga.co.uk':
                return 'preprod';
            case 'localhost':
                return 'local';
            default:
                return 'ws';
        }
    }

    return { ...appConfig.authentication, ...appConfig.authenticationEnvs[getEnvironment()] };
};

const authConfig = getAuthConfig();

const cookieParameters = {
    secure: true,
    domain: authConfig.cookies.authorizationDomain
};

function generateRandomString() {
    let string = '';
    const stringLength = 16;
    const possibleChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    for (let i = 0; i < stringLength; i += 1) {
        string += possibleChars.charAt(Math.floor(Math.random() * possibleChars.length));
    }
    return string;
}

const getAuthUrl = () => {
    const nonceValue = generateRandomString();
    const stateValue = generateRandomString();
    const redirectUrl = new URL(CALLBACK_URL, window.location.href).href;

    const params = {
        response_type: authConfig.responseType,
        response_mode: authConfig.responseMode,
        client_id: authConfig.clientId,
        nonce: nonceValue,
        redirect_uri: redirectUrl,
        state: stateValue,
        scope: authConfig.scope
    };

    const cookieNames = {
        nonce: authConfig.cookies.nonce,
        state: authConfig.cookies.state
    };

    cookie.set(cookieNames.state, stateValue, cookieParameters);
    cookie.set(cookieNames.nonce, nonceValue, cookieParameters);

    const serializedParams = queryString.stringify(params);
    return `${authConfig.url}${authConfig.endpoints.authorize}?${serializedParams}`;
};

const logout = async () => {
    const requestHeader = {
        method: 'GET',
        mode: authConfig.logoutMode,
        credentials: 'include', // add cookies
    };
    try {
        await Promise.all([
            fetch(authConfig.endpoints.CEPLogout, requestHeader),
            fetch(authConfig.endpoints.SagaGroupSignout, requestHeader),
            fetch(authConfig.url + authConfig.endpoints.logout, requestHeader)
        ]);
    } catch (e) {
        console.error(e);
    } finally {
        SgSessionStorageService.removeItem(SgSessionStorageService.KEY.IS_AUTHORIZED);
        window.location.href = authConfig.endpoints.logoutRedirect;
    }
};

function useSgAuthentication(pageInitialized) {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const history = useHistory();
    const location = useLocation();
    const isError = location.hash.includes('/error');
    const forceRefreshToHomePage = SgSessionStorageService.getItem(SgSessionStorageService.KEY.FORCE_CEP_REFRESH_TO_HOME_PAGE);

    useEffect(() => {
        events.on(REQUEST_UNAUTHORIZED_EVENT, logout);
        return () => {
            events.removeListener(logout);
        };
    }, []);

    if (!isAuthenticated && location.hash.includes('/auth/authorize')) {
        setIsAuthenticated(true);
        SgSessionStorageService.setItem(SgSessionStorageService.KEY.IS_AUTHORIZED, true);
        const startingPage = SgSessionStorageService.getItem(SgSessionStorageService.KEY.ORIGINAL_PAGE) || '/';
        history.push(startingPage);
    }


    if (!isAuthenticated && pageInitialized && !isError) {
        SgSessionStorageService.setItem(SgSessionStorageService.KEY.ORIGINAL_PAGE, window.location.pathname + window.location.hash);
        if (SgSessionStorageService.getItem(SgSessionStorageService.KEY.IS_AUTHORIZED)) {
            setIsAuthenticated(true);
        } else {
            window.location.href = getAuthUrl();
        }
    }

    if (forceRefreshToHomePage) {
        history.push('/#/home');
        SgSessionStorageService.removeItem(SgSessionStorageService.KEY.FORCE_CEP_REFRESH_TO_HOME_PAGE);
    }

    return {
        isAuthenticated: isAuthenticated,
        isError: isError
    };
}

export {
    useSgAuthentication,
    logout
};
